import { createContext, useContext, useEffect } from "react";
import { useThemePreference } from "../components/base/AppsSidebar/hooks/useThemePreference";
import { brand } from "../config/theme";

export type ThemeMode = "light" | "dark" | "system";

export interface ThemeContextType {
  isDarkMode: boolean;
  themeMode: ThemeMode;
  setTheme: (theme: ThemeMode) => void;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const { themeMode, isDarkMode, setTheme } = useThemePreference();

  // El tema queda visible para el CSS global (index.css lo usa para que el
  // texto verde sea legible sobre fondos claros). Los componentes siguen
  // recibiendo isDarkMode por props como siempre.
  useEffect(() => {
    const root = document.documentElement;
    root.dataset.theme = isDarkMode ? "dark" : "light";
    // --brand-text: el primario como TEXTO. Sólo si el tema define un tono
    // para fondos claros; si no, no se toca nada (los demás temas quedan igual).
    if (brand.primaryTextLight) {
      root.dataset.brandText = "";
      root.style.setProperty("--brand-text", isDarkMode ? brand.primary : brand.primaryTextLight);
    } else {
      delete root.dataset.brandText;
      root.style.removeProperty("--brand-text");
    }
  }, [isDarkMode]);

  const toggleTheme = () => {
    if (themeMode === "system") {
      setTheme(isDarkMode ? "light" : "dark");
    } else {
      setTheme(themeMode === "dark" ? "light" : "dark");
    }
  };

  return (
    <ThemeContext.Provider value={{ isDarkMode, themeMode, setTheme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextType {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used within a ThemeProvider");
  return ctx;
}

/**
 * @deprecated Use useTheme() instead. This alias is provided for backward compatibility.
 */
export function useDarkMode(): ThemeContextType {
  return useTheme();
}
